import { useEffect, useState } from "react";
import { Download, FileText, Beef, Bird, Fish, PawPrint } from "lucide-react";
import { api, API } from "@/lib/api";

const SPECIES_ICON = {
  Cattle: Beef,
  Poultry: Bird,
  Aqua: Fish,
  Pet: PawPrint,
};

function resolveUrl(file_url) {
  if (!file_url) return "#";
  if (file_url.startsWith("http")) return file_url;
  return `${API.replace(/\/api$/, "")}${file_url}`;
}

/**
 * Renders downloadable catalogue cards. Pass dark={true} when used on a dark section.
 * Optionally filter to specific species.
 */
export default function CataloguesSection({
  title = "Download Our Catalogues",
  subtitle = "Species-specific product catalogues with formulations, pack sizes and technical information.",
  dark = false,
  hideHeader = false,
  testIdPrefix = "catalogue",
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api
      .get("/brochures")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  const sectionBg = dark ? "bg-[#0B2545] text-white" : "bg-[#F8FAFC] text-[#0F172A]";
  const cardBg = dark
    ? "bg-white/[0.04] border border-white/10 hover:bg-white/[0.07]"
    : "bg-white border border-slate-200 hover:shadow-xl";
  const overlineCol = dark ? "text-[#C8A14B]" : "text-[#2A9D8F]";

  return (
    <section id="catalogues" className={`section-padding ${sectionBg}`}>
      <div className="container-fr">
        {!hideHeader && (
          <div className="max-w-3xl mb-12">
            <span className={`overline ${overlineCol}`}>Product Catalogues</span>
            <h2 className={`h-section mt-3 ${dark ? "text-white" : ""}`}>{title}</h2>
            <p className={`mt-4 max-w-2xl ${dark ? "text-slate-300" : "text-slate-600"}`}>
              {subtitle}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((b, i) => {
            const Icon = SPECIES_ICON[b.species] || FileText;
            const url = resolveUrl(b.file_url);
            return (
              <a
                key={b.id}
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testIdPrefix}-card-${(b.species || b.title).toLowerCase()}`}
                className={`group rounded-xl p-7 transition-all duration-300 hover:-translate-y-1 fade-up ${cardBg}`}
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${dark ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#0B2545] text-[#2A9D8F]"}`}>
                    <Icon size={22} />
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded ${dark ? "bg-[#C8A14B]/15 text-[#C8A14B]" : "bg-[#C8A14B]/15 text-[#8a6a23]"}`}>PDF</div>
                </div>
                <h3 className={`font-display text-xl font-bold mt-5 leading-snug ${dark ? "text-white" : "text-[#0B2545]"}`}>
                  {b.title}
                </h3>
                {b.description && (
                  <p className={`text-sm mt-3 leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
                    {b.description}
                  </p>
                )}
                <div className={`inline-flex items-center gap-2 mt-6 text-sm font-semibold ${dark ? "text-[#2A9D8F]" : "text-[#2A9D8F]"} group-hover:gap-3 transition-all`}>
                  <Download size={16} /> Download PDF
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
